<template>
  <!-- The geo panel. Every bit of the trigger and panel behaviour lives in
       HoverPopover, which ValuePopover shares. -->
  <HoverPopover :trigger-label="triggerLabel" :panel-label="panelLabel" :side="side" :delay="delay" panel-class="w-72">
    <slot />

    <template #content>
      <!-- Loading state -->
      <div v-if="loading" class="animate-pulse text-xs text-muted-foreground">
        {{ t("ipinfo.loading") }}
      </div>

      <!-- Private/local IP -->
      <div v-else-if="ipInfo?.is_private" class="flex flex-col gap-2">
        <div v-if="props.ip" class="break-all font-mono text-xs text-muted-foreground">
          {{ props.ip }}
        </div>
        <div class="text-sm text-muted-foreground">
          {{ t("ipinfo.private_ip") }}
        </div>
      </div>

      <!-- Not found state (IPInfo DB available but no data for this IP) -->
      <div v-else-if="notFound && ipInfoAvailable" class="text-xs text-muted-foreground">
        {{ t("ipinfo.not_found") }}
      </div>

      <!-- Content when data available -->
      <div v-else-if="ipInfo" class="flex flex-col gap-2">
        <!-- IP address -->
        <div v-if="props.ip" class="break-all font-mono text-xs text-muted-foreground">
          {{ props.ip }}
        </div>

        <!-- Country row: flag + country name + continent -->
        <div v-if="ipInfo.country || ipInfo.country_code" class="flex items-center gap-2">
          <span class="text-lg leading-none">{{ countryCodeToFlag(ipInfo.country_code) }}</span>
          <span class="text-sm font-medium text-foreground">
            {{ ipInfo.country ?? ipInfo.country_code
            }}<span v-if="ipInfo.continent" class="font-normal text-muted-foreground">, {{ ipInfo.continent }}</span>
          </span>
        </div>

        <!-- ASN details (compact label-value layout) -->
        <div
          v-if="ipInfo.asn || ipInfo.as_name || ipInfo.as_domain"
          class="mt-1 flex flex-col gap-1 border-t border-border pt-2"
        >
          <div v-if="ipInfo.asn" class="flex items-baseline gap-2">
            <span class="w-14 shrink-0 text-xs text-muted-foreground">{{ t("ipinfo.asn") }}</span>
            <span class="text-sm text-foreground">{{ ipInfo.asn }}</span>
          </div>
          <div v-if="ipInfo.as_name" class="flex items-baseline gap-2">
            <span class="w-14 shrink-0 text-xs text-muted-foreground">{{ t("ipinfo.as_name") }}</span>
            <span class="text-sm text-foreground">{{ ipInfo.as_name }}</span>
          </div>
          <div v-if="ipInfo.as_domain" class="flex items-baseline gap-2">
            <span class="w-14 shrink-0 text-xs text-muted-foreground">{{ t("ipinfo.as_domain") }}</span>
            <span class="text-sm text-foreground">{{ ipInfo.as_domain }}</span>
          </div>
        </div>
      </div>

      <!-- Fallback: just show the full IP (e.g. IPInfo DB unavailable, truncated display) -->
      <div v-else-if="props.ip" class="break-all font-mono text-xs text-muted-foreground">
        {{ props.ip }}
      </div>
    </template>
  </HoverPopover>
</template>

<script setup lang="ts">
// The geo panel only. HoverPopover owns the trigger, the floating panel and
// every hover / tap / Escape / click-outside rule, which ValuePopover shares.
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { countryCodeToFlag } from "../../utils/countryCodeToFlag";
import { ipInfoAvailable, type IPInfoData } from "../../composables/useIPInfo";
import HoverPopover from "./HoverPopover.vue";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    ip?: string;
    ipInfo?: IPInfoData | null;
    loading?: boolean;
    notFound?: boolean;
    side?: "top" | "bottom" | "left" | "right";
    delay?: number;
  }>(),
  {
    ip: undefined,
    ipInfo: null,
    loading: false,
    notFound: false,
    side: "bottom",
    delay: 200,
  },
);

// aria-label replaces the button's text content as its accessible name, so the
// address has to be in the label or a screen reader never hears it.
const triggerLabel = computed(() =>
  props.ip ? t("ipinfo.popover_label_for_ip", { ip: props.ip }) : t("ipinfo.popover_label"),
);
const panelLabel = computed(() => t("ipinfo.popover_label"));
</script>
